import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { useFavorites } from "@/hooks/useFavorites";
import SearchInput from "@/components/base/SearchInput";
import FavoritesDrawer from "@/components/feature/FavoritesDrawer";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `relative whitespace-nowrap px-1 py-2 text-sm font-medium transition-colors duration-200 ${
    isActive ? "text-primary-600" : "text-foreground-700 hover:text-primary-600"
  }`;

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { count } = useFavorites();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  return (
    <header className="sticky top-0 z-40 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 w-full items-center gap-4 px-4 md:px-6 lg:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-film-fill text-lg" aria-hidden="true" />
          </span>
          <span className="flex flex-col leading-none">
            <strong className="text-base font-black tracking-tight text-foreground-950">韩国三级电影</strong>
            <span className="mt-0.5 text-[10px] tracking-widest text-foreground-500">韩影资料库</span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-6 md:flex">
          <NavLink to="/" end className={navLinkClass}>
            首页
          </NavLink>
          <div className="relative" onMouseLeave={() => setCategoryOpen(false)}>
            <button
              type="button"
              onMouseEnter={() => setCategoryOpen(true)}
              onClick={() => setCategoryOpen((v) => !v)}
              className="flex items-center gap-1 whitespace-nowrap px-1 py-2 text-sm font-medium text-foreground-700 transition-colors duration-200 hover:text-primary-600"
              aria-expanded={categoryOpen}
            >
              分类
              <i className="ri-arrow-down-s-line text-base" aria-hidden="true" />
            </button>
            {categoryOpen ? (
              <div className="absolute left-0 top-full z-30 w-[320px] rounded-lg border border-background-200 bg-background-50 p-2 animate-fade-up">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="flex items-start gap-3 rounded-md p-2.5 transition-colors duration-200 hover:bg-primary-50"
                  >
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                      <i className={`${cat.icon} text-base`} aria-hidden="true" />
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <strong className="text-sm font-bold text-foreground-900">{cat.name}</strong>
                      <span className="mt-0.5 line-clamp-1 text-xs text-foreground-500">{cat.description}</span>
                    </span>
                  </Link>
                ))}
              </div>
            ) : null}
          </div>
          <NavLink to="/search" className={navLinkClass}>
            搜索
          </NavLink>
          <NavLink to="/about" className={navLinkClass}>
            关于
          </NavLink>
        </nav>

        <div className="ml-auto hidden w-full max-w-xs lg:block">
          <SearchInput size="sm" />
        </div>

        <Link
          to="/search"
          aria-label="搜索影片"
          className="ml-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-foreground-700 transition-colors duration-200 hover:bg-background-200 hover:text-primary-600 lg:hidden"
        >
          <i className="ri-search-line text-lg" aria-hidden="true" />
        </Link>

        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label="查看我的收藏"
          className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-foreground-700 transition-colors duration-200 hover:bg-background-200 hover:text-primary-600"
        >
          <i className="ri-bookmark-3-line text-lg" aria-hidden="true" />
          {count > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-bold text-background-50">
              {count}
            </span>
          ) : null}
        </button>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开菜单"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-foreground-800 transition-colors duration-200 hover:bg-background-200 md:hidden"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true" />
        </button>
      </div>

      {menuOpen ? (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <SearchInput size="sm" className="mb-4" />
          <nav className="flex flex-col">
            <NavLink to="/" end className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600">
              首页
            </NavLink>
            <NavLink to="/search" className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600">
              搜索
            </NavLink>
            <NavLink to="/about" className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600">
              关于
            </NavLink>
          </nav>
          <p className="mt-3 px-3 text-xs font-bold uppercase tracking-wider text-foreground-400">分类导航</p>
          <div className="mt-1 grid grid-cols-2 gap-2 px-1">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                className="flex items-center gap-2 rounded-md bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600"
              >
                <i className={`${cat.icon} text-base text-primary-500`} aria-hidden="true" />
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      <FavoritesDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </header>
  );
}